import { useEffect, useState, type FormEvent } from 'react';
import {
  MAX_NICKNAME_LENGTH,
  MAX_ROOM_NAME_LENGTH,
  MESSAGE_RETENTION_DAYS,
} from '@sokdaq/protocol';
import { normalizeInviteCode } from '@sokdaq/room-core';
import { avatarFor } from '../avatar.js';
import { ApiError, createRoom, lookupRoom } from '../net/api.js';
import { loadIdentity, saveIdentity } from '../store/persist.js';

type Mode = 'join' | 'create';

export interface JoinScreenProps {
  /** 초대코드로 방에 들어간다. 신원은 이미 저장된 뒤다. */
  onEnter: (code: string, nickname: string) => void;
  /** 직전 시도가 실패했다면 그 이유. */
  notice?: string | null;
}

export function JoinScreen({ onEnter, notice }: JoinScreenProps) {
  const [mode, setMode] = useState<Mode>('join');
  const [nickname, setNickname] = useState('');
  const [code, setCode] = useState('');
  const [roomName, setRoomName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // 지난번에 쓰던 닉네임을 채워 둔다. 매번 다시 입력하게 하면
  // "30초 안에 대화 시작"이라는 목표에 어긋난다.
  useEffect(() => {
    void loadIdentity().then((identity) => {
      if (identity) setNickname(identity.nickname);
    });
  }, []);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;

    const trimmedNickname = nickname.trim();
    if (trimmedNickname === '') {
      setError('닉네임을 입력해 주세요');
      return;
    }

    // 사람이 입력한 코드는 표준형으로 되돌린 뒤에 쓴다. 그대로 들고
    // 다니면 머리글에 'sokdaq7f3k92qx' 같은 게 걸리고, 그걸 복사해
    // 남에게 전달하게 된다. 형식 오류도 왕복 없이 여기서 걸러진다.
    let targetCode: string;
    if (mode === 'join') {
      const normalized = normalizeInviteCode(code);
      if (normalized === null) {
        setError('초대코드 형식이 올바르지 않습니다 (예: SOKDAQ-7F3K-92QX)');
        return;
      }
      targetCode = normalized;
    } else {
      targetCode = '';
    }

    setBusy(true);
    setError(null);

    try {
      if (mode === 'create') {
        targetCode = (await createRoom(roomName.trim())).code;
      } else {
        // 입장 전에 방이 있는지 확인한다. 없는 코드로 WebSocket을 열면
        // 연결 실패로만 보여서 원인을 알기 어렵다.
        await lookupRoom(targetCode);
      }

      await saveIdentity({ nickname: trimmedNickname, avatar: avatarFor(trimmedNickname) });
      onEnter(targetCode, trimmedNickname);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : '연결할 수 없습니다. 잠시 후 다시 시도해 주세요');
      setBusy(false);
    }
  }

  return (
    <div className="app-shell flex items-center justify-center overflow-y-auto bg-neutral-950 p-6 text-neutral-100">
      <div className="w-full max-w-sm">
        <h1 className="text-2xl font-semibold tracking-tight">속닥</h1>
        <p className="mt-1 text-sm text-neutral-400">사무실 한 켠의 작은 대화방</p>

        {notice && (
          <p className="mt-4 rounded border border-amber-900 bg-amber-950/50 px-3 py-2 text-sm text-amber-200">
            {notice}
          </p>
        )}

        <div className="mt-6 flex gap-1 rounded-lg bg-neutral-900 p-1" role="tablist">
          <TabButton active={mode === 'join'} onClick={() => setMode('join')}>
            초대코드로 입장
          </TabButton>
          <TabButton active={mode === 'create'} onClick={() => setMode('create')}>
            새 방 만들기
          </TabButton>
        </div>

        <form onSubmit={handleSubmit} className="mt-4 flex flex-col gap-3">
          <Field label="닉네임">
            <input
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              maxLength={MAX_NICKNAME_LENGTH}
              placeholder="김대리"
              autoFocus
              className={inputClass}
            />
          </Field>

          {mode === 'join' ? (
            <Field label="초대코드">
              <input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="SOKDAQ-7F3K-92QX"
                autoCapitalize="characters"
                spellCheck={false}
                className={`${inputClass} font-mono`}
              />
            </Field>
          ) : (
            <Field label="방 이름">
              <input
                value={roomName}
                onChange={(e) => setRoomName(e.target.value)}
                maxLength={MAX_ROOM_NAME_LENGTH}
                placeholder="기획팀 뒷자리"
                className={inputClass}
              />
            </Field>
          )}

          {error && <p className="text-sm text-red-400">{error}</p>}

          <button
            type="submit"
            disabled={busy}
            className="mt-1 rounded-lg bg-neutral-100 px-4 py-2 font-medium text-neutral-900 hover:bg-white disabled:opacity-50"
          >
            {busy ? '연결 중…' : mode === 'create' ? '만들고 입장' : '입장'}
          </button>
        </form>

        <p className="mt-6 text-xs text-neutral-500">
          회원가입은 없습니다. 닉네임만 정하면 바로 들어갑니다.
        </p>

        {/*
          들어가기 전에 보여야 하는 말이다.

          이 앱의 용도가 뒷담화라서, 쓰는 사람은 "속닥이니까 둘만 보겠지"라고
          짐작하기 쉽다. 실제로는 서버를 띄운 사람이 전부 읽을 수 있다(§6.5).
          설정 안에만 적어 두면 아무도 안 보므로 입장 직전에 한 번 말한다.

          neutral-500(위 문단)보다 밝은 400을 쓴다. 안내가 아니라 알아야 하는
          사실이라 곁다리처럼 보이면 안 된다. 그렇다고 모달로 막지는 않는다 —
          매번 확인을 누르게 하면 세 번째부터는 읽지 않고 닫는다.
        */}
        <p className="mt-2 text-xs leading-relaxed text-neutral-400">
          종단간 암호화가 아닙니다. 주고받는 말은{' '}
          <span className="text-neutral-300">이 서버를 띄운 사람이 읽을 수 있습니다.</span>{' '}
          {MESSAGE_RETENTION_DAYS}일 뒤 자동으로 지워지지만, 곤란해질 이야기는 남기지 마세요.
        </p>
      </div>
    </div>
  );
}

const inputClass =
  'w-full rounded-lg border border-neutral-800 bg-neutral-900 px-3 py-2 text-sm outline-none placeholder:text-neutral-600 focus:border-neutral-600';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs text-neutral-400">{label}</span>
      {children}
    </label>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`flex-1 rounded px-3 py-1.5 text-sm ${
        active ? 'bg-neutral-800 text-neutral-100' : 'text-neutral-400 hover:text-neutral-200'
      }`}
    >
      {children}
    </button>
  );
}
